import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import InputField from './InputField';

jest.mock('components/styleguide/icon/Icon', () => {
  return {
    __esModule: true,
    default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
  };
});

describe('InputField', () => {
  it('renders input field', () => {
    render(<InputField />);
    expect(screen.getByRole('textbox')).toBeInTheDocument();
  });

  it('renders label when provided', () => {
    render(<InputField label='Email' />);
    expect(screen.getByLabelText('Email')).toBeInTheDocument();
  });

  it('renders mandatory asterisk', () => {
    render(<InputField label='Email' required={true} />);
    expect(screen.getByText('*')).toBeInTheDocument();
  });

  it('renders hint text when focused', () => {
    render(<InputField hint='This is a hint' />);
    fireEvent.focus(screen.getByRole('textbox'));
    expect(screen.getByText('This is a hint')).toBeInTheDocument();
  });

  it('renders disabled state', () => {
    render(<InputField disabled={true} />);
    expect(screen.getByRole('textbox')).toBeDisabled();
  });

  it('renders error state', () => {
    render(<InputField error={'Error message'} />);
    expect(screen.getByText('Error message')).toBeInTheDocument();
  });

  it('applies error class', () => {
    const { container } = render(<InputField error={'Error message'} />);
    expect(container.querySelector('.error')).toBeInTheDocument();
  });

  it('renders custom placeholder', () => {
    render(<InputField placeholder='Enter your name' />);
    expect(screen.getByPlaceholderText('Enter your name')).toBeInTheDocument();
  });

  it('renders left icon', () => {
    const { container } = render(<InputField iconLeft="link" />);
    expect(container.querySelector('.iconLeftContainer')).toBeInTheDocument();
  });

  it('renders right icon', () => {
    const { container } = render(<InputField iconRight="eye" />);
    expect(container.querySelector('.iconRightContainer')).toBeInTheDocument();
  });

  it('renders large size', () => {
    const { container } = render(<InputField size='L' />);
    expect(container.querySelector('.large')).toBeInTheDocument();
  });

  describe('accessibility', () => {
    it('sets aria-invalid when error is present', () => {
      render(<InputField error='Required' />);
      expect(screen.getByRole('textbox')).toHaveAttribute('aria-invalid', 'true');
    });

    it('does not set aria-invalid when no error', () => {
      render(<InputField />);
      expect(screen.getByRole('textbox')).toHaveAttribute('aria-invalid', 'false');
    });

    it('associates error message with input via aria-describedby', () => {
      render(<InputField id='email' error='Invalid email' />);
      const input = screen.getByRole('textbox');
      expect(input).toHaveAttribute('aria-describedby', 'email-message');
      expect(document.getElementById('email-message')).toHaveTextContent('Invalid email');
    });

    it('associates hint with input via aria-describedby when focused', () => {
      render(<InputField id='name' hint='Enter full name' />);
      const input = screen.getByRole('textbox');
      fireEvent.focus(input);
      expect(input).toHaveAttribute('aria-describedby', 'name-message');
      expect(document.getElementById('name-message')).toHaveTextContent('Enter full name');
    });

    it('does not set aria-describedby when no hint or error', () => {
      render(<InputField id='plain' />);
      expect(screen.getByRole('textbox')).not.toHaveAttribute('aria-describedby');
    });

    it('renders error with alert role', () => {
      render(<InputField error='Something went wrong' />);
      expect(screen.getByRole('alert')).toHaveTextContent('Something went wrong');
    });

    it('does not render alert role for hint', () => {
      render(<InputField hint='Just a hint' />);
      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });

    it('hides required asterisk from screen readers', () => {
      render(<InputField label='Email' required />);
      const asterisk = screen.getByText('*');
      expect(asterisk).toHaveAttribute('aria-hidden', 'true');
    });

    it('sets required attribute on input', () => {
      render(<InputField required />);
      expect(screen.getByRole('textbox')).toBeRequired();
    });
  });

  describe('error sync', () => {
    it('syncs displayError when error prop changes', () => {
      const { rerender } = render(<InputField error='First error' />);
      expect(screen.getByText('First error')).toBeInTheDocument();

      rerender(<InputField error='Second error' />);
      expect(screen.getByText('Second error')).toBeInTheDocument();
    });

    it('clears displayError when error prop is removed', () => {
      const { rerender } = render(<InputField error='Error' />);
      expect(screen.getByText('Error')).toBeInTheDocument();

      rerender(<InputField />);
      expect(screen.queryByText('Error')).not.toBeInTheDocument();
    });

    it('shows validation error on blur when required and empty', () => {
      render(<InputField required defaultValidationMessage='Required field' />);
      const input = screen.getByRole('textbox');
      fireEvent.blur(input);
      expect(screen.getByText('Required field')).toBeInTheDocument();
    });

    it('clears validation error on change', () => {
      render(<InputField required defaultValidationMessage='Required field' />);
      const input = screen.getByRole('textbox');
      fireEvent.blur(input);
      expect(screen.getByText('Required field')).toBeInTheDocument();

      fireEvent.change(input, { target: { value: 'hello' } });
      expect(screen.queryByText('Required field')).not.toBeInTheDocument();
    });
  });
});
